import { integrationApi } from "@repo/api";
import { InputField, Typography } from "@saviynt/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../constants/theme";
import { useIntegrationById } from "../../../hooks/useIntegrationById";
import { useIntegrationTypeFeatures } from "../../../hooks/useIntegrationTypeFeatures";
import { useTestConnection } from "../../../hooks/useTestConnection";
import {
  type Frequency,
  DEFAULT_FREQUENCY,
  parseCronToSchedule,
} from "../../../utils/schedule";
import { AdditionalConfigurationSection } from "../../aws/AWSIntegrationForm/AdditionalConfigurationSection";
import { getDefaultTime } from "../../aws/AWSIntegrationForm/utils";
import { FormSection } from "../../FormSection";
import { IntegrationTypeSection } from "../../shared/IntegrationTypeSection";
import { TestConnectionBlock } from "../../TestConnectionBlock";

export interface CopilotStudioDiscoveryFormData {
  name: string;
  description: string;
  tenantId: string;
  clientId: string;
  clientSecret: string;
  dataverseUrl: string;
  isValid: boolean;
  cftDownloaded: boolean;
  importTime?: Date | null;
}

interface CopilotStudioDiscoveryFormProps {
  integrationType: string;
  integrationId?: number;
  onFormDataChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

export function CopilotStudioDiscoveryForm({
  integrationType,
  integrationId,
  onFormDataChange,
}: CopilotStudioDiscoveryFormProps) {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [dataverseUrl, setDataverseUrl] = useState("");
  const [clientSecretVisible, setClientSecretVisible] = useState(false);
  const [importTime, setImportTime] = useState<Date | null>(() =>
    getDefaultTime(),
  );
  const [frequency, setFrequency] = useState<Frequency>(DEFAULT_FREQUENCY);
  const hasPrefilledIntegrationRef = useRef(false);
  const hasPrefilledCredentialsRef = useRef(false);
  const onFormDataChangeRef = useRef(onFormDataChange);
  onFormDataChangeRef.current = onFormDataChange;

  const { data: integrationData } = useIntegrationById(integrationId);
  const { data: credentials } = useQuery({
    queryKey: ["integrationCredentials", integrationId],
    queryFn: async () => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.getIntegrationCredentials(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: featuresResponse } =
    useIntegrationTypeFeatures(integrationType);
  const featureNames = useMemo(
    () =>
      featuresResponse?.features
        ? featuresResponse.features.map((f) => f.featureName)
        : [],
    [featuresResponse?.features],
  );

  useEffect(() => {
    if (!integrationId || !integrationData) return;

    if (!hasPrefilledIntegrationRef.current) {
      setName(integrationData.displayName ?? "");
      setDescription((integrationData.metadata?.description as string) ?? "");
      setTenantId(
        (integrationData.metadata?.tenantId as string) ??
          (integrationData.metadata?.accountId as string) ??
          "",
      );
      setDataverseUrl((integrationData.metadata?.dataverseUrl as string) ?? "");
      if (
        integrationData.features?.length &&
        integrationData.features[0].schedules?.length
      ) {
        const cronExpression =
          integrationData.features[0].schedules[0].cronExpression;
        if (cronExpression) {
          const parsed = parseCronToSchedule(cronExpression);
          setFrequency(parsed.frequency);
          if (parsed.time) {
            setImportTime(parsed.time);
          }
        }
      }
      hasPrefilledIntegrationRef.current = true;
    }

    // Credentials API response: { type, data: { clientTenantId, clientId, clientSecret, dataverseUrl }, metadata: {} }
    const creds = credentials as
      | {
          data?: {
            clientTenantId?: string;
            clientId?: string;
            clientSecret?: string;
            dataverseUrl?: string;
          };
        }
      | undefined;
    const credsData = creds?.data;
    if (creds && !hasPrefilledCredentialsRef.current) {
      if (
        credsData?.clientTenantId != null &&
        credsData.clientTenantId !== ""
      ) {
        setTenantId(credsData.clientTenantId);
      }
      if (credsData?.clientId != null && credsData.clientId !== "") {
        setClientId(credsData.clientId);
      }
      if (credsData?.clientSecret != null && credsData.clientSecret !== "") {
        setClientSecret(credsData.clientSecret);
      }
      if (credsData?.dataverseUrl != null && credsData.dataverseUrl !== "") {
        setDataverseUrl(credsData.dataverseUrl);
      }
      hasPrefilledCredentialsRef.current = true;
    }
  }, [integrationId, integrationData, credentials]);

  const testConnection = useTestConnection(() => {
    const trimmedTenantId = tenantId.trim();
    const trimmedClientId = clientId.trim();
    const trimmedClientSecret = clientSecret.trim();
    const trimmedDataverseUrl = dataverseUrl.trim();
    if (
      !trimmedTenantId ||
      !trimmedClientId ||
      !trimmedClientSecret ||
      !trimmedDataverseUrl
    ) {
      return null;
    }
    return {
      integrationType: "copilotstudio",
      credentials: {
        type: "OAUTH2",
        data: {
          clientId: trimmedClientId,
          clientSecret: trimmedClientSecret,
          clientTenantId: trimmedTenantId,
          dataverseUrl: trimmedDataverseUrl,
        },
        metadata: {},
      },
      integrationParameters: {},
    };
  });

  const isNameValid = name.trim().length > 0;
  const isTenantIdValid = tenantId.trim().length > 0;
  const isClientIdValid = clientId.trim().length > 0;
  const isClientSecretValid =
    clientSecret.trim().length > 0 || (!!integrationId && !!clientId);
  const isDataverseUrlValid = dataverseUrl.trim().length > 0;
  const isValid =
    isNameValid &&
    isTenantIdValid &&
    isClientIdValid &&
    isClientSecretValid &&
    isDataverseUrlValid &&
    (frequency !== "24hours" || importTime !== null);
  const cftDownloaded =
    isTenantIdValid &&
    isClientIdValid &&
    isClientSecretValid &&
    isDataverseUrlValid;

  useEffect(() => {
    onFormDataChangeRef.current({
      name: name.trim(),
      description: description.trim(),
      tenantId: tenantId.trim(),
      clientId: clientId.trim(),
      clientSecret: clientSecret,
      dataverseUrl: dataverseUrl.trim(),
      frequency,
      importTime,
      isValid,
      cftDownloaded,
      featureNames,
      integrationType,
    });
  }, [
    name,
    description,
    tenantId,
    clientId,
    clientSecret,
    dataverseUrl,
    frequency,
    importTime,
    isValid,
    cftDownloaded,
    featureNames,
    integrationType,
  ]);

  return (
    <div
      className="Integrations-addIntegrationForm flex flex-col h-full w-full p-4"
      data-testid="copilot-studio-discovery-form"
    >
      <div className="flex-1 overflow-auto p-4">
        <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
          <IntegrationTypeSection integrationType={integrationType} />
          <FormSection
            icon="Connect"
            title={intl.formatMessage({
              id: "6c0sHj",
              defaultMessage:
                "What are the connection details of your integration?",
              description: "Connection details section title",
            })}
            subtitle={intl.formatMessage({
              id: "KlVUsw",
              defaultMessage:
                "Some fields have been pre-filled for you, you can update them whenever you like.",
              description: "Connection details section subtitle",
            })}
          >
            <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
              <InputField
                name="name"
                label={
                  <Typography kind="label" htmlFor="copilot-studio-name">
                    <FormattedMessage
                      id="JeJjeJ"
                      defaultMessage="Integration Name"
                      description="Integration name field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={name}
                setValue={setName}
                isRequired
                isDisabled={!!integrationId}
              />

              <div>
                <Typography
                  kind="label"
                  className="block mb-1"
                  htmlFor="copilot-studio-description"
                >
                  <FormattedMessage
                    id="TjUCGI"
                    defaultMessage="Integration Description"
                    description="Integration description field label"
                  />
                </Typography>
                <InputField
                  name="description"
                  placeholder=""
                  value={description}
                  setValue={setDescription}
                  kind="multiline"
                  rows={4}
                />
              </div>

              <InputField
                name="tenantId"
                label={
                  <Typography kind="label" htmlFor="copilot-studio-tenant-id">
                    <FormattedMessage
                      id="LG3Bq9"
                      defaultMessage="Tenant ID"
                      description="Tenant ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={tenantId}
                setValue={setTenantId}
                isRequired
              />

              <InputField
                name="clientId"
                label={
                  <Typography kind="label" htmlFor="copilot-studio-client-id">
                    <FormattedMessage
                      id="YB3Qau"
                      defaultMessage="Client ID"
                      description="Client ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={clientId}
                setValue={setClientId}
                isRequired
              />

              <InputField
                name="clientSecret"
                label={
                  <Typography
                    kind="label"
                    htmlFor="copilot-studio-client-secret"
                  >
                    <FormattedMessage
                      id="Og17uQ"
                      defaultMessage="Client Secret"
                      description="Client Secret field label"
                    />
                  </Typography>
                }
                placeholder=""
                type={clientSecretVisible ? "text" : "password"}
                value={clientSecret}
                setValue={setClientSecret}
                isRequired={!integrationId}
                suffixButtonType="password"
                suffixIsToggle={clientSecretVisible}
                suffixOnClick={() =>
                  setClientSecretVisible(!clientSecretVisible)
                }
              />

              <InputField
                name="dataverseUrl"
                label={
                  <Typography
                    kind="label"
                    htmlFor="copilot-studio-dataverse-url"
                  >
                    <FormattedMessage
                      id="n1nzEw"
                      defaultMessage="Dataverse URL"
                      description="Dataverse URL field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={dataverseUrl}
                setValue={setDataverseUrl}
                isRequired
              />

              <TestConnectionBlock
                isTestingConnection={testConnection.isTestingConnection}
                connectionStatus={testConnection.connectionStatus}
                onTestConnection={testConnection.handleTestConnection}
                isDisabled={
                  !isTenantIdValid ||
                  !isClientIdValid ||
                  !clientSecret.trim() ||
                  !isDataverseUrlValid
                }
              />
            </div>
          </FormSection>

          <AdditionalConfigurationSection
            initialTime={importTime}
            initialFrequency={frequency}
            onScheduleChange={(freq, time) => {
              setFrequency(freq as Frequency);
              setImportTime(time);
            }}
            isRequired={true}
            isCritical={frequency === "24hours" && importTime === null}
          />
        </div>
      </div>
    </div>
  );
}
